import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import useLocaleNavigate from 'utils/customHooks/useLocaleNavigate';
import { buildLocalizedPathname, getLocaleFromPathname } from 'utils/localePath';
import { useSpring } from 'react-spring';
import delay from 'delay';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
// api
import { logoutApi } from 'api/login';
import { getMetadataCompany, getCategoryMenuNotPaginated } from 'api/company';
// stores
import { useUserStore, useCartStore, useCompanyStore, useBlogCategoriesStore, useEbooksStore } from 'stores';
// helpers
import { unSetLocal } from 'helpers';
// components
import NavbarOffCanvas from 'components/navbar/NavbarOffCanvas';
import NavbarCollapse from 'components/navbar/NavbarCollapse';
import NavBarSecondary from 'components/navbar/NavBarSecondary';
import MenuBurgerMobile from './MenuBurgerMobile';
import SearchBarMenu from './SearchBarMenu';

const MenuMain = ({ isDesktop = true, helpTextList = [] }) => {
  const location = useLocation();
  const navigate = useLocaleNavigate();
  
  // Zustand stores
  const { benefitedBookstore, logoutSuccess, setIsWorker, setEmail, setFirstName, setLastName, setBenefitedBookstore, setBillingAddress, setShippingAddress } = useUserStore();
  const { countItems, clear, setShippingCartAddress } = useCartStore();
  const { loadMetaCompany } = useCompanyStore();
  const { categories: blogCategories } = useBlogCategoriesStore();
  const { clearEbooksData } = useEbooksStore();

  const [showToast, setShowToast] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showMiniSearchBar, setShowMiniSearchBar] = useState(false);
  const [showOffCanvas, setShowOffCanvas] = useState(false);
  const [secondaryImage, setSecondaryImage] = useState(false);
  const previousCounterValue = useRef(countItems);
  const [activeKey, setActiveKey] = useState('');

  const [counterAnimProps, set] = useSpring(() => ({
    backgroundColor: 'white',
    transform: 'translateY(0px)',
    config: {
      tension: 160,
      friction: 20,
    },
  }));

  const isScrollingRef = useRef(false);

  useEffect(() => {
    isScrollingRef.current = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveKey(location.search ? `${location.pathname}${location.search}` : location.pathname);
    const timer = setTimeout(() => {
      isScrollingRef.current = false;
      setIsSticky(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  useScrollPosition(
    ({ currPos }) => {
      if (isScrollingRef.current) return;
      const isShow = currPos.y !== 0;
      if (isShow !== isSticky) setIsSticky(isShow);
    },
    [isSticky],
  );

  useEffect(() => {
    (async () => {
      if (countItems !== previousCounterValue.current) {
        set({
          backgroundColor: 'rgb(145, 214, 255)',
          transform: 'translateY(-2px)',
        });
        await delay(180);
        setShowToast(true);
        set({ backgroundColor: 'white', transform: 'translateY(0px)' });
      }
    })();
  }, [countItems, set]);

  useEffect(() => {
    (async () => {
      if (showToast) {
        await delay(4000);
        setShowToast(false);
      }
    })();
  }, [showToast]);

  useEffect(() => {
    if (benefitedBookstore.url !== '' && benefitedBookstore.url !== undefined) {
      setSecondaryImage(true);
    }
  }, [benefitedBookstore]);

  const closeSession = async () => {
    const homePath = buildLocalizedPathname('/', getLocaleFromPathname(location.pathname));
    const { status } = await logoutApi({
      refresh_token: localStorage.getItem('refresh'),
    });
    if (status === 200) {
      unSetLocal('access');
      unSetLocal('refresh');
      unSetLocal('permissions');
      unSetLocal('type_user');
      setSecondaryImage(false);
      setIsWorker(false);
      setEmail('');
      setFirstName('');
      setLastName('');
      setBenefitedBookstore({});
      setBillingAddress({});
      setShippingAddress({});
      setShippingCartAddress({});
      clear();
      clearEbooksData();
      logoutSuccess();
      const metadataCompany = await getMetadataCompany();
      if (metadataCompany.status === 200) {
        const matters = await getCategoryMenuNotPaginated({
          fields: 'id,name,description,genre_ibic,padre_id',
        });
        if (matters.status === 200) {
          const results = {
            ...metadataCompany.data,
            matters: matters.data,
          };
          loadMetaCompany(results);
        } else {
          loadMetaCompany(metadataCompany.data);
        }
      }
    }
    setShowOffCanvas(false);
    navigate(homePath, { replace: true });
  };

  return (
    <header className={`header ${isSticky ? 'header-fixed' : ''}`}>
      <div className={`header__nav container-full ${isSticky ? 'nav-fixed' : ''}`}>
        <Navbar className="bg-transparency header__main-nav p-0 container-fluid" expand="lg" expanded={false}>
          <NavBarSecondary isSticky={isSticky} isDesktop={isDesktop} activeKey={activeKey} helpTextList={helpTextList} />

          {/* BARRA MENU HAMBURGUESA MOVIL */}
          <MenuBurgerMobile
            showSearchBar={showSearchBar}
            isSticky={isSticky}
            isDesktop={isDesktop}
            counterAnimProps={counterAnimProps}
            secondaryImage={secondaryImage}
            setShowOffCanvas={setShowOffCanvas}
            setShowSearchBar={setShowSearchBar}
          />

          {/* MENU BARRA BUSQUEDA  */}
          <SearchBarMenu
            showSearchBar={showSearchBar}
            isDesktop={isDesktop}
            isSticky={isSticky}
            secondaryImage={secondaryImage}
          />

          {/* MENU NAVEGACION CATEGORIAS */}
          {isDesktop && (
            <NavbarCollapse
              isDesktop={isDesktop}
              isSticky={isSticky}
              secondaryImage={secondaryImage}
              blogCategories={blogCategories}
              showMiniSearchBar={showMiniSearchBar}
              counterAnimProps={counterAnimProps}
              closeSession={closeSession}
              setShowMiniSearchBar={setShowMiniSearchBar}
              activeKey={activeKey}
            />
          )}

          {/* MENU NAVEGACION CATEGORIAS MOBILE*/}
          {!isDesktop && showOffCanvas && (
            <NavbarOffCanvas
              isDesktop={isDesktop}
              showOffCanvas={showOffCanvas}
              blogCategories={blogCategories}
              closeSession={closeSession}
              setShowOffCanvas={setShowOffCanvas}
              activeKey={activeKey}
            />
          )}
        </Navbar>
      </div>
    </header>
  );
};

export default MenuMain;
